'use client';
import { createMachine, assign } from 'xstate';
import logger from '../../logger';
import { uploadFilesToCloud } from '../cloud/attachments';
import { fetchNoteFromCloud } from '../cloud/notes';

/**
 * attachmentsMachine — manages the file-upload lifecycle for a single note.
 *
 * States:
 *   idle        — waiting for input
 *   uploading   — POSTing files to /upload (multipart)
 *   refreshing  — re-fetching the note to get updated attachments array
 *   done        — upload + note refresh succeeded
 *   error       — upload or refresh failed
 *
 * Context:
 *   note_id     — the note being uploaded to
 *   files       — the File[] array being uploaded
 *   uploadedNote — the refreshed note object after success (or null)
 *   error       — the Error from the failed step (or null)
 *
 * Usage: invoked from appState_applicationReadyState.js uploadingFiles state node
 */
export const attachmentsMachine = createMachine(
  {
    id: 'attachmentsMachine',
    predictableActionArguments: true,
    schema: {
      context: /** @type {{ note_id: string|null, files: File[], uploadedNote: object|null, error: Error|null }} */ ({}),
    },
    context: {
      note_id: null,
      files: [],
      uploadedNote: null,
      error: null,
    },
    initial: 'uploading',
    states: {
      uploading: {
        invoke: {
          id: 'uploadFiles',
          src: (context) => uploadFilesToCloud({
            note_id: context.note_id,
            files: context.files,
          }),
          onDone: {
            target: 'refreshing',
            actions: assign(() => ({
              error: null,
            })),
          },
          onError: {
            target: 'error',
            actions: assign((context, event) => {
              logger.error(event.data, 'attachmentsMachine: upload failed');
              return { error: event.data };
            }),
          },
        },
      },
      refreshing: {
        invoke: {
          id: 'refreshNote',
          src: (context) => fetchNoteFromCloud({ noteId: context.note_id }),
          onDone: {
            target: 'done',
            actions: assign((context, event) => ({
              uploadedNote: event.data,
            })),
          },
          onError: {
            // Non-fatal: upload succeeded even if refresh failed
            target: 'done',
            actions: assign((context, event) => {
              logger.error(event.data, 'attachmentsMachine: note refresh failed (non-fatal)');
              return {};
            }),
          },
        },
      },
      done: {
        type: 'final',
        data: (context) => ({
          uploadedNote: context.uploadedNote,
          note_id: context.note_id,
        }),
      },
      error: {
        type: 'final',
        data: (context) => ({
          error: context.error,
          note_id: context.note_id,
        }),
      },
    },
  },
);
